import Axios, { AxiosResponse } from "axios"
import {
  CategoryDetail,
  CategoryRequest,
  ParamsProjectsProps,
  ProjectCount,
  ProjectDetail,
  ProjectStatusKey,
  ProjectSyncOrganizationResponse,
  OrganizationProjectDataResponse,
  SyncCommitToOrganizationRequest,
  SyncCommitToOrganizationResponse,
} from "../types"
import { EncryptionKeyInput, KeyEncryptionType, STATUS_RESPONSE } from "types"
import { MESSENGER_NOTIFICATION } from "constants/messenger"
import { ProjectComponentDetail } from "pages/project-component/types"
import {
  BuildConversationDetail,
  ConversationDetail,
} from "pages/conversations/conversations.type"
import {
  encryptionController,
  EncryptionKeys,
  ProjectSensitiveData,
  UpdateProjectSensitiveDataRequest,
} from "controllers/EncryptionController"
import { encryptionHelper } from "helpers/encryption"
import { activityLogHelper, ActivityLogType } from "helpers/activity_log"
import { isEmpty, isEqual } from "lodash"
import { encryptionGCMHelper } from "helpers/encryption-gcm"
import { pushTo } from "helpers/history"
import { PATH } from "constants/path"
import { getPrivateKeyByUserId } from "helpers/utils"
import forge from "node-forge"

export const getCategoriesMiddleware = async (
  url: string,
  params?: CategoryRequest
) => {
  const response: AxiosResponse<{
    data: CategoryDetail[]
  }> = await Axios.get(url, {
    params,
  })
  return response.data.data
}
export const getProjectCountMiddleware = async (groupId?: string) => {
  const response: AxiosResponse<{
    data: ProjectCount
  }> = await Axios.get(`/api/project/get-project-count`, {
    params: {
      group_id: groupId,
    },
  })
  return response.data.data
}

export const postCategoryMiddleware = (
  request: {
    name: string
  },
  callback: (
    type: STATUS_RESPONSE,
    messenger: string,
    data?: CategoryDetail
  ) => void
) => {
  Axios.post(`/api/category/create`, request)
    .then(
      (
        response: AxiosResponse<{
          data: CategoryDetail
        }>
      ) => {
        callback(STATUS_RESPONSE.SUCCESS, "", response.data.data)
      }
    )
    .catch((error) => {
      callback(
        STATUS_RESPONSE.ERROR,
        error.response?.data?.message ??
          MESSENGER_NOTIFICATION.POST_CATEGORY_ERROR
      )
    })
}

export const getProjectsMiddleware = async (
  params: ParamsProjectsProps,
  url: string
) => {
  const response: AxiosResponse<{
    data: ProjectDetail[]
    total?: number
    encryptedKeys?: any
  }> = await Axios.get(url, {
    params,
  })
  const projectEncryptionKeys = JSON.parse(
    localStorage.getItem(EncryptionKeys.projectEncryptionKeys) || "{}"
  )
  if (response.data.encryptedKeys) {
    const userVaultKey = localStorage.getItem(
      EncryptionKeys.userVaultKey
    ) as string
    const decryptedKeys: Record<string, string> = {}
    for (const el of response.data.encryptedKeys) {
      decryptedKeys[el.id] = await encryptionGCMHelper.decryptContext(
        userVaultKey,
        el.encryptedKey
      )
    }

    localStorage.setItem(
      EncryptionKeys.projectEncryptionKeys,
      JSON.stringify({
        ...projectEncryptionKeys,
        ...decryptedKeys,
      })
    )
  }
  return {
    data: response.data.data,
    total: response.data.total,
  }
}
export const getUnEncryptedProjectsMiddleware = async () => {
  const response: AxiosResponse<{
    data: ProjectDetail[]
    total: number
    done: number
  }> = await Axios.get("/api/project/get-list-un-encrypted")

  return {
    data: response.data.data,
    total: response.data.total,
    done: response.data.done,
  }
}
export const updateFailedFileMiddleware = async (fileKey: string) => {
  await Axios.put("/api/project/update-failed-files?loading=0", { fileKey })
  return true
}
export const postFileHashKeyMiddleware = async (
  fileKey: string,
  hashKey: string
) => {
  await Axios.post("/api/file/hash?loading=0", {
    file_key: fileKey,
    hash_key: hashKey,
  })
  return true
}
export const getFileHashKeyMiddleware = async (fileKey: string) => {
  const response = await Axios.get(
    `/api/file/hash?file_key=${encodeURIComponent(fileKey)}&loading=0`
  )

  return {
    hash_key: response.data.hash_key,
    public_key: response.data.public_key,
  }
}

export const getProjectDetailMiddleware = async (
  idProject: string,
  idConversation?: string,
  isDecrypt = true,
  isLoading = false,
  dbKey?: string
) => {
  let url = `/api/project/${idProject}${isLoading ? `?loading=0` : ""}`
  if (idConversation) {
    url += `?conversation_id=${idConversation}`
  }
  const response: AxiosResponse<{
    data: ProjectDetail
  }> = await Axios.get(url, {
    headers: dbKey
      ? {
          "x-db-key": dbKey,
        }
      : {},
  })
  if (response.data.data.sync_type || response.data.data.is_moved) {
    pushTo(PATH.projects)
    return response.data.data
  }
  if (response.data.data.is_encrypted && isDecrypt) {
    let projectEncryptionKey = await encryptionController().decryptProjectKey(
      response.data.data.encryptedKey
    )
    if (
      projectEncryptionKey === response.data.data.encryptedKey ||
      String(projectEncryptionKey).length !== 64
    ) {
      projectEncryptionKey = ""
    }
    const projectKeys = JSON.parse(
      localStorage.getItem(EncryptionKeys.projectEncryptionKeys) || "{}"
    )
    localStorage.setItem(
      EncryptionKeys.currentProjectEncryptionKey,
      isEmpty(projectEncryptionKey) ? "" : (projectEncryptionKey as string)
    )
    localStorage.setItem(
      EncryptionKeys.projectEncryptionKeys,
      JSON.stringify({
        ...projectKeys,
        [response.data.data.id]: projectEncryptionKey,
      })
    )
  } else {
    localStorage.setItem(EncryptionKeys.currentProjectEncryptionKey, "")
  }
  return response.data.data
}
export const getProjectSensitiveDataMiddleware = async (idProject: string) => {
  let url = `/api/project/${idProject}/sensitive?loading=0`
  const response: AxiosResponse<{
    data: ProjectSensitiveData
  }> = await Axios.get(url)

  return response.data.data
}
export const getProjectFilesMiddleware = async (idProject: string) => {
  let url = `/api/project/${idProject}/files?loading=0`
  const response: AxiosResponse<{
    data: any
  }> = await Axios.get(url)

  return response.data.data
}
export const postSyncProjectBinaryFileMiddleware = async (
  idProject: string,
  payload: any
) => {
  let url = `/api/project/${idProject}/sync-binary-file?loading=0`
  return Axios.post(url, payload)
}

export const getProjectKeyMiddleware = async (idProject: string) => {
  let url = `/api/encryption/project/${idProject}/project-key`
  const response: AxiosResponse<{
    data: any
  }> = await Axios.get(url)

  return response.data.data
}
export const putProjectSyncContextDataMiddleware = async (
  idProject: string,
  data: UpdateProjectSensitiveDataRequest
) => {
  let url = `/api/project/${idProject}/update-sensitive?loading=0`
  return Axios.put(url, data).catch((error) => {
    console.log(error)
  })
}
export const createEncryptionKeyMiddleware = async (
  data: EncryptionKeyInput
) => {
  try {
    let url = `/api/auth/key-encryption`
    await Axios.post(url, data)
  } catch (error) {}
}
export const createEncryptionKeysMiddleware = async (
  data: EncryptionKeyInput[],
  isLoading = false,
  dbKey?: string
) => {
  try {
    let url = `/api/auth/key-encryptions${isLoading ? `?loading=0` : ""}`
    await Axios.post(
      url,
      { data },
      { headers: dbKey ? { "x-db-key": dbKey } : {} }
    )
    return true
  } catch (error) {
    return false
  }
}
export const validateAccessTokenMiddleware = async (token: string) => {
  try {
    let url = `/api/auth/verify-token?token=${token}`
    const response = await Axios.get(url)
    return response.data.isValid
  } catch (error) {}
}
export const acceptedSharingProjectMiddleware = async (projectId: string) => {
  try {
    let url = `/api/project/${projectId}/accepted`
    await Axios.post(url)
  } catch (error) {}
}
export const acceptedSharingConversationMiddleware = async (
  conversationId: string,
  dbKey: string
) => {
  console.log("dbKey", dbKey)
  try {
    let url = `/api/project-build/accept-conversation/${conversationId}`

    await Axios.post(
      url,
      {},
      {
        headers: dbKey ? { "x-db-key": dbKey } : {},
      }
    )
  } catch (error) {}
}

export const postProjectMiddleware = async (
  request: {
    name: string
    description: string
    category_ids: string[]
    group_id?: string
  },
  callback: (
    type: STATUS_RESPONSE,
    messenger: string,
    projectKey: string,
    projectId: string
  ) => void
) => {
  const projectEncryptionKey = encryptionHelper.createRandomKey()

  const userVaultKey = localStorage.getItem(
    EncryptionKeys.userVaultKey
  ) as string

  let dataToPost: any = request
  const encryptedEncryptionKey = (await encryptionGCMHelper.encryptContext(
    userVaultKey,
    projectEncryptionKey
  )) as string
  dataToPost = {
    ...request,
    encryptedKey: encryptedEncryptionKey,
    log: await encryptionController().encrypt(
      activityLogHelper.toLogMessage(ActivityLogType.CreateProject, {
        projectName: request.name,
      }),
      {
        dataType: "string",
        type: "project",
        encryptionKey: projectEncryptionKey,
      }
    ),
  }

  Axios.post(`/api/project/create?loading=0`, dataToPost)
    .then(async (response: AxiosResponse) => {
      const projectEncryptionKeys = JSON.parse(
        localStorage.getItem(EncryptionKeys.projectEncryptionKeys) || "{}"
      )
      localStorage.setItem(
        EncryptionKeys.projectEncryptionKeys,
        JSON.stringify({
          ...projectEncryptionKeys,
          [response.data.data.id]: projectEncryptionKey,
        })
      )
      callback(
        STATUS_RESPONSE.SUCCESS,
        MESSENGER_NOTIFICATION.POST_PROJECT_SUCCESS,
        projectEncryptionKey,
        response.data.data.id
      )
    })
    .catch((error) => {
      callback(
        STATUS_RESPONSE.ERROR,
        error.response?.data?.message ??
          MESSENGER_NOTIFICATION.POST_PROJECT_ERROR,
        projectEncryptionKey,
        ""
      )
    })
}
const toUpdateProjectLogMessage = async (project: any, payload: any) => {
  const messages: string[] = []
  if (payload.name.toLowerCase() !== project.name.toLowerCase()) {
    messages.push(`project name to <b>${payload.name}</b>`)
  }
  if (payload.description.toLowerCase() !== project.description.toLowerCase()) {
    messages.push(`project description to <b>${payload.description}</b>`)
  }
  const projectTags = project.selectCategories.map((item) => item.id)
  if (!isEqual(payload.category_ids, projectTags)) {
    const categories = await Axios.get(
      `/api/category/get-many?ids=${payload.category_ids.join(",")}`
    )
    const tags = categories.data.data.map((item) => item.name).join(", ")
    messages.push(`project tags to <b>${tags}</b>`)
  }
  return messages.join(", ")
}
export const putProjectMiddleware = async (
  idProject: string,
  request: {
    name: string
    description: string
    category_ids: string[]
  },
  callback: (
    type: STATUS_RESPONSE,
    messenger: string,
    data?: ProjectDetail
  ) => void,
  projectData?: any
) => {
  const temp = await toUpdateProjectLogMessage(projectData, request)
  const encryptionKey = JSON.parse(
    localStorage.getItem(EncryptionKeys.projectEncryptionKeys) || "{}"
  )[idProject]
  const encrypted = await encryptionController().encrypt(
    `<b>${localStorage.getItem("userEmail")}</b> has changed ${temp}`,
    {
      dataType: "string",
      type: "project",
      encryptionKey: encryptionKey,
    }
  )
  Axios.put(`/api/project/${idProject}/update`, {
    ...request,
    log: encrypted,
  })
    .then(
      (
        response: AxiosResponse<{
          data: ProjectDetail
        }>
      ) => {
        callback(
          STATUS_RESPONSE.SUCCESS,
          MESSENGER_NOTIFICATION.PUT_PROJECT_SUCCESS,
          response.data.data
        )
      }
    )
    .catch((error) => {
      callback(
        STATUS_RESPONSE.ERROR,
        error.response?.data?.message ??
          MESSENGER_NOTIFICATION.PUT_PROJECT_ERROR
      )
    })
}

export const deleteProjectMiddleware = (
  idProject: string,
  callback: (type: STATUS_RESPONSE, messenger: string) => void
) => {
  Axios.delete(`/api/project/${idProject}/delete`)
    .then((_response: AxiosResponse) => {
      callback(
        STATUS_RESPONSE.SUCCESS,
        MESSENGER_NOTIFICATION.DELETE_PROJECT_SUCCESS
      )
    })
    .catch((error) => {
      callback(
        STATUS_RESPONSE.ERROR,
        error.response?.data?.message ??
          MESSENGER_NOTIFICATION.DELETE_PROJECT_ERROR
      )
    })
}
export const archiveProjectMiddleware = async (
  idProject: string,
  isArchived: number,
  callback: (
    type: STATUS_RESPONSE,
    messenger: string,
    data?: ProjectDetail
  ) => void,
  projectName: string
) => {
  Axios.post(`/api/project/${idProject}/archive`, {
    log: await activityLogHelper.toEncryptedMessage(
      ActivityLogType.ArchivedProject,
      {
        archive: isArchived ? "unarchived" : "archived",
        projectName,
      }
    ),
  })
    .then(
      (
        response: AxiosResponse<{
          data: ProjectDetail
        }>
      ) => {
        callback(
          STATUS_RESPONSE.SUCCESS,
          isArchived === 0
            ? MESSENGER_NOTIFICATION.ARCHIVE_PROJECT_SUCCESS
            : MESSENGER_NOTIFICATION.UN_ARCHIVE_PROJECT_SUCCESS,
          response.data.data
        )
      }
    )
    .catch((error) => {
      const newMessageError =
        isArchived === 0
          ? MESSENGER_NOTIFICATION.ARCHIVE_PROJECT_ERROR
          : MESSENGER_NOTIFICATION.UN_ARCHIVE_PROJECT_ERROR
      callback(
        STATUS_RESPONSE.ERROR,
        error.response?.data?.message
          ? error.response?.data?.message
          : newMessageError
      )
    })
}
export const favoriteProjectMiddleware = async (
  idProject: string,
  projectName: string,
  isFavorite: boolean,
  callback: (
    type: STATUS_RESPONSE,
    messenger: string,
    data?: ProjectDetail
  ) => void
) => {
  const log = await activityLogHelper.toEncryptedMessage(
    ActivityLogType.LikeProject,
    {
      projectName,
      like: isFavorite ? "unlike" : "like",
    }
  )
  Axios.post(`/api/project/${idProject}/like-or-unlike`, {
    log,
  })
    .then(
      (
        response: AxiosResponse<{
          data: ProjectDetail
        }>
      ) => {
        callback(STATUS_RESPONSE.SUCCESS, "", response.data.data)
      }
    )
    .catch((error) => {
      callback(
        STATUS_RESPONSE.ERROR,
        error.response?.data?.message ??
          MESSENGER_NOTIFICATION.FAVORITE_PROJECT_ERROR
      )
    })
}

export const getProjectRoleMiddleware = async (projectId: string) => {
  const response: AxiosResponse<{
    data: { role: string }
  }> = await Axios.get(`/api/project/${projectId}/role`)
  return response.data.data
}

export const putUpdateStatusMiddleware = async (
  idProject: string,
  request: {
    status: number
  },
  callback: (
    type: STATUS_RESPONSE,
    messenger: string,
    data?: ProjectDetail
  ) => void
) => {
  Axios.put(`/api/project/${idProject}/update-status`, {
    ...request,
    log: await activityLogHelper.toEncryptedMessage(
      ActivityLogType.ChangeProjectStatus,
      {
        projectStatus: ProjectStatusKey[request.status],
      }
    ),
  })
    .then(
      (
        response: AxiosResponse<{
          data: ProjectDetail
        }>
      ) => {
        callback(
          STATUS_RESPONSE.SUCCESS,
          MESSENGER_NOTIFICATION.PUT_UPDATE_STATUS_PROJECT_SUCCESS,
          response.data.data
        )
      }
    )
    .catch((error) => {
      callback(
        STATUS_RESPONSE.ERROR,
        error.response?.data?.message ??
          MESSENGER_NOTIFICATION.PUT_UPDATE_STATUS_PROJECT_ERROR
      )
    })
}

export const getObjectSearchMiddleware = async (
  params: ParamsProjectsProps
) => {
  const response: AxiosResponse<{
    data: {
      projects: ProjectDetail[]
      components: ProjectComponentDetail[]
      builds: BuildConversationDetail[]
      conversations: ConversationDetail[]
    }
  }> = await Axios.get(`/api/project/search`, {
    params,
  })
  return response.data.data
}

export const deleteProjectAttachment = async (id: string) => {
  return Axios.delete(`/api/project/${id}/delete-attachment`).then(
    (res) => res.data
  )
}

export const deleteEncryptionKeysMiddleware = async (
  data: {
    relation_id: string
    type: number
    additional_id?: string
    model?: string
  }[]
) => {
  try {
    let url = `/api/auth/key-encryptions`
    await Axios.delete(url, { data })
  } catch (error) {}
}

export const syncProjectToOrganizationMiddleware = async (
  projectId: string,
  sourceId: string,
  sourceDbName: string,
  destinationId: string,
  destinationDbName: string,
  log: string,
  projectCode: string
) => {
  return Axios.post(`/api/project/${projectId}/sync-to-organization`, {
    sourceId,
    sourceDbName,
    destinationId,
    destinationDbName,
    log,
    projectCode,
  }).then((res) => res.data)
}

export const moveProjectToOrganizationMiddleware = async (
  id: string,
  organization_id: string
) => {
  return Axios.post(`/api/project/${id}/move-to-organization`, {
    organization_id,
  }).then((res) => res.data)
}

export const getProjectSyncOrganizationsMiddleware = async (
  projectId: string
): Promise<ProjectSyncOrganizationResponse> => {
  const response: AxiosResponse<ProjectSyncOrganizationResponse> =
    await Axios.get(`/api/project/${projectId}/sync-organization?loading=0`)
  return response.data
}

export const getOrganizationProjectDataMiddleware = async (
  projectId: string,
  sourceId: string,
  sourceDbName: string,
  destinationId: string,
  destinationDbName: string
): Promise<OrganizationProjectDataResponse> => {
  const response: AxiosResponse<OrganizationProjectDataResponse> =
    await Axios.get(`/api/project/${projectId}/organization/data`, {
      params: {
        sourceId,
        sourceDbName,
        destinationId,
        destinationDbName,
      },
    })
  return response.data
}

export const syncCommitToOrganizationMiddleware = async (
  projectId: string,
  payload: SyncCommitToOrganizationRequest
): Promise<SyncCommitToOrganizationResponse> => {
  const response: AxiosResponse<SyncCommitToOrganizationResponse> =
    await Axios.post(
      `/api/project/${projectId}/sync-commit-to-organization?loading=0`,
      payload
    )
  return response.data
}

export const handleUserShareKeyMiddleware = async (
  projectId: string,
  destination_db_name: string,
  source_db_name: string
) => {
  try {
    const response: AxiosResponse<{
      data: {
        users: {
          id: string
          email: string
          publicKey: string
        }[]
        projectKey: {
          relationId: string
          encryptedKey: string
          type: number
        }
      }
    }> = await Axios.get(`/api/project/${projectId}/user-share-key?loading=0`, {
      params: {
        destination_db_name,
        source_db_name,
      },
    })
    const result = response.data.data
    const projectKey = result.projectKey
    const users = result.users
    if (users.length && projectKey) {
      const userVaultKey = localStorage.getItem(
        EncryptionKeys.userVaultKey
      ) as string
      const privateKeyPem: any = await getPrivateKeyByUserId()
      let decrypted = projectKey.encryptedKey
      if (projectKey.type === KeyEncryptionType.ProjectShareKey) {
        decrypted = privateKeyPem.decrypt(projectKey.encryptedKey, "RSA-OAEP")
      } else {
        decrypted = await encryptionGCMHelper.decryptContext(
          userVaultKey,
          projectKey.encryptedKey
        )
      }
      if (decrypted && decrypted !== projectKey.encryptedKey) {
        const encryptionKeys: EncryptionKeyInput[] = []
        users.forEach((user) => {
          const publicKeyFromPem = forge.pki.publicKeyFromPem(user.publicKey)
          try {
            const encryptedKey = publicKeyFromPem.encrypt(decrypted, "RSA-OAEP")
            encryptionKeys.push({
              encrypted_key: encryptedKey,
              relation_id: projectId,
              type: KeyEncryptionType.ProjectShareKey,
              user_id: user.id,
            })
          } catch (error) {}
        })
        if (encryptionKeys.length) {
          await createEncryptionKeysMiddleware(
            encryptionKeys,
            true,
            destination_db_name
          )
        }
      }
    }

    return true
  } catch (error) {
    return false
  }
}

export const handleUserShareKeyProjectMiddleware = async (
  is_share_all_project: boolean,
  loading: boolean,
  project_ids?: string,
  group_id?: string,
  user_ids?: string,
  dbName?: string
) => {
  try {
    const params: Record<string, string | boolean> = {
      is_share_all_project,
    }
    if (project_ids) {
      params.project_ids = project_ids
    }
    if (group_id) {
      params.group_id = group_id
    }
    if (user_ids) {
      params.user_ids = user_ids
    }
    if (dbName) {
      params.dbName = dbName
    }
    const response: AxiosResponse<{
      data: {
        relation_id: string
        encrypted_key: string
        type: number
        users: {
          user_id: string
          public_key: string
        }[]
      }[]
    }> = await Axios.get(
      `/api/user/share-project-key?${loading ? "loading=0" : ""}`,
      {
        params,
      }
    )
    const projectKeys = response.data.data
    const userVaultKey = localStorage.getItem(
      EncryptionKeys.userVaultKey
    ) as string
    const privateKeyPem: any = await getPrivateKeyByUserId()
    const encryptionKeys: EncryptionKeyInput[] = []
    if (projectKeys.length) {
      for (const elProjectKey of projectKeys) {
        let decrypted = elProjectKey.encrypted_key
        if (elProjectKey.type === KeyEncryptionType.ProjectShareKey) {
          decrypted = privateKeyPem.decrypt(
            elProjectKey.encrypted_key,
            "RSA-OAEP"
          )
        } else {
          decrypted = await encryptionGCMHelper.decryptContext(
            userVaultKey,
            elProjectKey.encrypted_key
          )
        }
        if (decrypted && decrypted !== elProjectKey.encrypted_key) {
          for (const elUserKey of elProjectKey.users) {
            const publicKeyFromPem = forge.pki.publicKeyFromPem(
              elUserKey.public_key
            )
            const encryptedKey = publicKeyFromPem.encrypt(decrypted, "RSA-OAEP")
            encryptionKeys.push({
              encrypted_key: encryptedKey,
              relation_id: elProjectKey.relation_id,
              type: KeyEncryptionType.ProjectShareKey,
              user_id: elUserKey.user_id,
            })
          }
        }
      }
    }
    if (encryptionKeys.length) {
      await createEncryptionKeysMiddleware(encryptionKeys, loading, dbName)
    }
    return true
  } catch (error) {
    return false
  }
}

export const addRoleGroupOrganizationMiddleware = async (dataRequest: {
  is_share_all_project: boolean
  project_ids?: string
  user_ids?: string
  group_ids?: string
  dbName?: string
}) => {
  const response = await Axios.post(
    `/api/project/add-role-group-organization?loading=0`,
    dataRequest
  )
  return response.data
}
